/**
 * Calculates start and end date strings for a given ISO week string (e.g., '2026-W22').
 * Used for custom tooltip displays in weekly charts.
 * 
 * @param {string} val 
 * @returns {{title: string, dateRange: string}|null}
 */
export function getWeeklyTooltipDetails(val) {
    if (typeof val === 'string' && val.includes('-W')) {
        const parts = val.split('-W');
        const year = parseInt(parts[0], 10);
        const week = parseInt(parts[1], 10);

        // ISO week Monday calculation
        const simple = new Date(year, 0, 4);
        const dayOfWeek = simple.getDay();
        const isoMondayOfJan4 = new Date(simple.getFullYear(), 0, 4 - ((dayOfWeek + 6) % 7));
        const weekStart = new Date(isoMondayOfJan4.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
        const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);

        const startMonth = weekStart.toLocaleString("en-US", { month: "short" });
        const startDate = weekStart.getDate();
        const endMonth = weekEnd.toLocaleString("en-US", { month: "short" });
        const endDate = weekEnd.getDate();

        const rangeStr = startMonth === endMonth
            ? `${startMonth} ${startDate} – ${endDate}, ${year}`
            : `${startMonth} ${startDate} – ${endMonth} ${endDate}, ${year}`;

        return {
            title: `Week ${week}, ${year}`,
            dateRange: rangeStr
        };
    }
    return null;
}

/**
 * Formats a raw week/month/day identifier string into a clean user-facing array
 * of [primaryLabel, secondaryLabel] (e.g., ["19-25 (May)", "2026"]).
 * Used as X-axis labels in charts.
 * 
 * @param {string} val 
 * @returns {string|[string, string]}
 */
export function formatWeekLabelOnXAxis(val) {
    if (typeof val === 'string') {
        // 1. Weekly: YYYY-Www  →  ["19-25 (May)", "2026"]
        if (val.includes('-W')) {
            const parts = val.split('-W');
            const year = parseInt(parts[0], 10);
            const week = parseInt(parts[1], 10);

            // ISO week Monday calculation
            const simple = new Date(year, 0, 4);
            const dayOfWeek = simple.getDay();
            const isoMondayOfJan4 = new Date(simple.getFullYear(), 0, 4 - ((dayOfWeek + 6) % 7));
            const weekStart = new Date(isoMondayOfJan4.getTime() + (week - 1) * 7 * 24 * 60 * 60 * 1000);
            const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);

            const startDay = weekStart.getDate();
            const startMonth = weekStart.toLocaleString("en-US", { month: "short" });
            const endDay = weekEnd.getDate();
            const endMonth = weekEnd.toLocaleString("en-US", { month: "short" });
            const endYear = weekEnd.getFullYear();

            const pad = (num) => num.toString().padStart(2, "0");
            // Same month: "19-25 (May)" / Cross-month: "28 Apr-04 May"
            const rangeLine = startMonth === endMonth
                ? `${pad(startDay)}-${pad(endDay)} (${endMonth})`
                : `${pad(startDay)} ${startMonth}-${pad(endDay)} ${endMonth}`;
            return [rangeLine, `${endYear}`];
        }

        // 2. Monthly: YYYY-MM  →  ["May", "2026"]
        if (/^\d{4}-\d{2}$/.test(val)) {
            const parts = val.split('-');
            const year = parts[0];
            const monthNum = parseInt(parts[1], 10) - 1;
            const dateObj = new Date(year, monthNum, 1);
            const monthName = dateObj.toLocaleString("en-US", { month: "short" });
            return [monthName, year];
        }

        // 3. Daily: YYYY-MM-DD  →  ["25 May", "2026"]
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
            const dateObj = new Date(val + 'T00:00:00');
            const day = dateObj.getDate();
            const monthName = dateObj.toLocaleString("en-US", { month: "short" });
            const year = dateObj.getFullYear();
            const pad = (num) => num.toString().padStart(2, "0");
            return [`${pad(day)} ${monthName}`, `${year}`];
        }
    }
    return val;
}

/**
 * Formats standard activities timestamps to MM DD, YYYY.
 * 
 * @param {string|Date} timestamp 
 * @returns {string}
 */
export const formatActivityTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
};

/**
 * Maps call status outcomes to premium UI style properties.
 * 
 * @param {string} status 
 * @returns {{label: string, bg: string, color: string}}
 */
export const getOutcomeStyles = (status) => {
    const s = String(status || "").toLowerCase().trim();
    if (s.includes("concluded") && !s.includes("not")) {
        return {
            label: "Call Concluded",
            bg: "#E8F5E9",
            color: "#2E7D32",
            dot: "#10B981"
        };
    }
    if (s.includes("connected") || s.includes("initiate")) {
        return {
            label: "Connected",
            bg: "#E3F2FD",
            color: "#1565C0",
            dot: "#3B82F6"
        };
    }
    if (s.includes("not interested")) {
        return {
            label: "Not Interested",
            bg: "#F3F0FD",
            color: "#7c3aed",
            dot: "#a855f7"
        };
    }
    if (s.includes("interested in future")) {
        return {
            label: "Interested in Future",
            bg: "#FFF3E0",
            color: "#EF6C00",
            dot: "#ff9f1c"
        };
    }
    if (s.includes("failed") || s.includes("fail")) {
        return {
            label: "Failed",
            bg: "#FFEBEE",
            color: "#C62828",
            dot: "#EF4444"
        };
    }
    // "Not Concluded" or others
    return {
        label: status || "Not Concluded",
        bg: "#EFF6FF",
        color: "#1d4ed8",
        dot: "#3b82f6"
    };
};

/**
 * Gets the initials of a name (max 2 characters).
 * 
 * @param {string} name 
 * @returns {string}
 */
export const getInitials = (name) => {
    if (!name) return "AD";
    const parts = name.split(" ");
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

/**
 * Generates premium background styling and text color based on username initials.
 * 
 * @param {string} name 
 * @returns {{bg: string, color: string}}
 */
export const getAvatarBg = (name) => {
    const s = String(name || "").toLowerCase().trim();
    if (s.includes("admin")) return { bg: "linear-gradient(135deg, #8A58EC 0%, #6f42c1 100%)", color: "#ffffff" };
    if (s.includes("himanshu")) return { bg: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)", color: "#ffffff" };
    if (s.includes("sandeep")) return { bg: "linear-gradient(135deg, #10B981 0%, #047857 100%)", color: "#ffffff" };
    return { bg: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", color: "#ffffff" };
};

/**
 * Gets premium progress bar styling color based on percentage bounds.
 * 
 * @param {number} pct 
 * @returns {string}
 */
export const getProgressColor = (pct) => {
    if (pct >= 50) return "#10B981"; // Emerald Green
    if (pct >= 30) return "#3B82F6"; // Electric Blue
    return "#6f42c1"; // Theme Purple
};
